import { Component, HostListener, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import { UploadFileService } from './upload/_services/upload-file.service';
import { UploadFileOtpService } from './pages/otp/services/upload-file-otp.service';
import { NotificationPersistenceService } from './_services/notification-persistence.service';
import { TooltipInjectorService } from './shared/services/tooltip-injector.service';
import { TooltipReanchorService } from './shared/services/tooltip-reanchor.service';
import { TicketUploadService } from './inspection-tickets/_services/ticket-upload.service';
import { AppUpdateService } from './_services/app-update.service';
import { TourGuideTooltipService } from './shared/services/tour-guide-tooltip.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import jwt_decode from 'jwt-decode';
import { TokenService } from './_services/token.service';
import { TenantInnerService } from './_services/tenant-inner.service';
import { environment } from '../environments/environment';
import { ADMIN_ROLE } from './_helpers/Constants';
import { PanoramerStateService } from './_services/panoramer-state.service';
import { SessionSyncService } from './_services/session-sync.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit {
  isMobile = false;
  isAdmin = false;
  private isSessionReplaced = false;

  constructor(
    private uploadFileService: UploadFileService,
    private uploadFileOtpService: UploadFileOtpService,
    private notificationPersistence: NotificationPersistenceService,
    private tooltipInjector: TooltipInjectorService,
    private tooltipReanchor: TooltipReanchorService,
    private ticketUpload: TicketUploadService,
    private appUpdateService: AppUpdateService,
    public tourGuideTooltipService: TourGuideTooltipService,
    private deviceService: DeviceDetectorService,
    private tokenService: TokenService,
    private tenantInnerService: TenantInnerService,
    private router: Router,
    private panoramerState: PanoramerStateService,
    private sessionSync: SessionSyncService
  ) {}

  ngOnInit(): void {
    this.notificationPersistence.displayPendingNotifications();
    this.appUpdateService.startPolling();
    this.tooltipReanchor.initialize();
    this.isMobile = this.deviceService.isMobile();
    this.isAdmin = this.checkAdminRole();
    // AppComponent is the bootstrap root and is never recreated by route
    // navigation, so without this, isAdmin stays stuck on whichever user was
    // logged in when the SPA first loaded, even after a later in-app login.
    this.tokenService.sessionStarted$.subscribe(() => {
      this.isAdmin = this.checkAdminRole();
    });
    // Another tab logged out or in as a different user: this tab's in-memory state belongs to the old session.
    // That tab already asked about running uploads (SessionGuardService).
    this.sessionSync.otherTabSessionChanged$.subscribe(() => this.reloadForNewSession());
    // This tab logged in or out while it uploads: SessionGuardService already asked, so stop the upload now, before
    // its next request goes out with the new user's token or with none.
    merge(this.tokenService.sessionStarted$, this.tokenService.sessionCleared$)
      .pipe(filter(() => this.isUploading()))
      .subscribe(() => this.reloadForNewSession());
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.isAdmin = this.checkAdminRole();
      this.tenantInnerService.syncTenantIdInUrl();
    });
  }

  private checkAdminRole(): boolean {
    const token = this.tokenService.getToken();
    if (!token) return false;
    try {
      const decoded: any = jwt_decode(token);
      const userRoles: string[] = decoded[environment.roleKey];
      return (Array.isArray(userRoles) && userRoles.includes(ADMIN_ROLE)) || (typeof userRoles === 'string' && userRoles === ADMIN_ROLE);
    } catch {
      return false;
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  beforeunloadHandler($event: any): boolean {
    if (this.isSessionReplaced) return true;
    if (this.isUploading()) {
      $event.returnValue = false;
      return false;
    }
    return true;
  }

  private isUploading(): boolean {
    return (
      this.uploadFileService.isFilesUploading$.value ||
      this.uploadFileOtpService.isFilesUploading$.value ||
      // Covers the whole ticket upload, incl. session setup, CAD processing and the final status save
      this.ticketUpload.isBusy() ||
      this.panoramerState.hasItems
    );
  }

  // The session this tab's state belongs to is gone, so the reload must not be cancellable: a cancelled reload would
  // leave the old screen sending the new user's token, or none.
  private reloadForNewSession(): void {
    this.isSessionReplaced = true;
    window.location.reload();
  }
}
